import { useState } from "react";
import { activeUsers, feedPosts as initialFeedPosts, type FeedPost } from "../../mocks/community";
import Navbar from "../../components/feature/Navbar";
import PeerFeedbackModal from "./components/PeerFeedbackModal";
import FailPostModal from "./components/FailPostModal";
import { usePeerFeedback } from "../../hooks/usePeerFeedback";

type Filter = "all" | "success" | "fail";
type Reactions = { [key: number]: { claps: number; fire: number; spark: number } };

/* ─────────────────────────────────────────────
   Fail Post Card
───────────────────────────────────────────── */
function FailCard({ post, onFeedback, reactionCounts, liked, onReact }: {
  post: FeedPost;
  onFeedback: () => void;
  reactionCounts: { claps: number; fire: number; spark: number };
  liked: Record<string, boolean>;
  onReact: (type: "claps" | "fire" | "spark") => void;
}) {
  const [promptOpen, setPromptOpen] = useState(false);

  return (
    <div className="bg-white rounded-3xl overflow-hidden border-2 border-red-100">
      {/* Fail header badge */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 px-4 py-2.5 flex items-center gap-2 border-b border-red-100">
        <span className="text-sm">💥</span>
        <span className="text-xs font-bold text-red-500 tracking-wide">실패 공유</span>
        <span className="ml-auto text-[10px] text-[#8B95A1]">Kolb 경험 학습</span>
      </div>

      {post.image && (
        <div className="relative">
          <img src={post.image} alt="fail result" className="w-full object-cover object-top"
            style={{ maxHeight: 160 }} />
          <div className="absolute inset-0 bg-red-500/10" />
        </div>
      )}

      <div className="p-4">
        {/* User */}
        <div className="flex items-center gap-2 mb-3">
          <img src={post.user.avatar} alt={post.user.name} className="w-7 h-7 rounded-full object-cover object-top" />
          <span className="text-xs font-bold text-[#191F28]">{post.user.name}</span>
          <span className="ml-auto text-[10px] text-[#191F28]/30">{post.time}</span>
        </div>

        {/* Content */}
        <p className="text-xs text-[#191F28]/70 leading-relaxed mb-3">{post.content}</p>

        {/* Failed prompt */}
        <button
          onClick={() => setPromptOpen((v) => !v)}
          className="w-full flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-3 cursor-pointer hover:bg-red-100 transition-colors"
        >
          <span className="text-[10px] font-bold text-red-500">실패한 프롬프트 보기</span>
          <i className={`ri-arrow-down-s-line text-red-400 text-sm transition-transform ${promptOpen ? "rotate-180" : ""}`} />
        </button>
        {promptOpen && (
          <div className="mb-3 bg-[#191F28] rounded-xl p-3">
            <code className="text-red-400 text-[11px] font-mono leading-relaxed break-words block">
              {post.failPrompt}
            </code>
          </div>
        )}

        {/* Fail reason */}
        {post.failReason && (
          <div className="flex gap-2 bg-[#FFF3E0] rounded-xl px-3 py-2.5 mb-3">
            <i className="ri-lightbulb-line text-[#FF8C00] text-sm flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-[#3D4756] font-medium leading-relaxed">{post.failReason}</p>
          </div>
        )}

        {/* Question to community */}
        {post.failQuestion && (
          <p className="text-[11px] font-bold text-[#191F28] mb-3 leading-snug">
            💬 {post.failQuestion}
          </p>
        )}

        {/* Reactions */}
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          {(["claps", "fire", "spark"] as const).map((type) => {
            const emoji = type === "claps" ? "👏" : type === "fire" ? "🔥" : "⚡";
            const isActive = liked[`${post.id}-${type}`];
            return (
              <button key={type} onClick={() => onReact(type)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  isActive ? "bg-red-100 text-red-500" : "bg-[#F2F4F6] text-[#191F28]/60 hover:bg-red-50"
                }`}>
                <span>{emoji}</span><span>{reactionCounts[type]}</span>
              </button>
            );
          })}
        </div>

        {/* Hint button */}
        <button onClick={onFeedback}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold border-2 border-red-200 text-red-500 hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap">
          <i className="ri-lightbulb-line text-sm" />
          💡 힌트 주기
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Normal Post Card
───────────────────────────────────────────── */
function NormalCard({ post, onFeedback, reviewCount, reactionCounts, liked, onReact, onCopyPrompt }: {
  post: FeedPost;
  onFeedback: () => void;
  reviewCount: number;
  reactionCounts: { claps: number; fire: number; spark: number };
  liked: Record<string, boolean>;
  onReact: (type: "claps" | "fire" | "spark") => void;
  onCopyPrompt?: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopyPrompt = () => {
    setCopied(true);
    onCopyPrompt?.();
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-3xl overflow-hidden">
      {post.image && (
        <img src={post.image} alt="post"
          className="w-full object-cover object-top"
          style={{ maxHeight: post.height === "tall" ? 280 : post.height === "medium" ? 200 : 160 }} />
      )}
      {post.stamp && (
        <div className="flex items-center justify-center py-8 bg-gradient-to-br from-[#F2F4F6] to-white">
          <div className="text-6xl animate-bounce">{post.stamp}</div>
        </div>
      )}
      <div className="p-4">
        {/* User */}
        <div className="flex items-center gap-2 mb-3">
          <img src={post.user.avatar} alt={post.user.name} className="w-7 h-7 rounded-full object-cover object-top" />
          <span className="text-xs font-bold text-[#191F28]">{post.user.name}</span>
          <span className="ml-auto text-[10px] text-[#191F28]/30">{post.time}</span>
        </div>
        {/* Text */}
        <p className="text-xs text-[#191F28]/70 leading-relaxed mb-4">{post.content}</p>

        {/* Reactions */}
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          {(["claps", "fire", "spark"] as const).map((type) => {
            const emoji = type === "claps" ? "👏" : type === "fire" ? "🔥" : "⚡";
            const isActive = liked[`${post.id}-${type}`];
            return (
              <button key={type} onClick={() => onReact(type)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  isActive ? "bg-[#00D873]/10 text-[#00D873]" : "bg-[#F2F4F6] text-[#191F28]/60 hover:bg-[#00D873]/10"
                }`}>
                <span>{emoji}</span><span>{reactionCounts[type]}</span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-2">
          {/* Peer Feedback */}
          <button onClick={onFeedback}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold border border-[#E4E6EA] text-[#8B95A1] hover:border-[#00D873] hover:text-[#00D873] hover:bg-[#00D873]/5 transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-chat-3-line text-sm" />
            피드백 주기
            {reviewCount > 0 && (
              <span className="bg-[#00D873]/10 text-[#00D873] px-1.5 py-0.5 rounded-full text-[10px] font-bold">{reviewCount}</span>
            )}
          </button>
          {/* Copy prompt — only for text/image posts */}
          {(post.type === "text" || post.type === "image") && (
            <button onClick={handleCopyPrompt}
              className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${
                copied ? "border-[#00D873] bg-[#00D873]/5 text-[#00D873]" : "border-[#E4E6EA] text-[#8B95A1] hover:border-[#191F28]/30 hover:text-[#191F28]"
              }`}>
              <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
              {copied ? "복사됨!" : "✨ 이 수익화 프롬프트 쓱- 가져오기"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
export default function Community() {
  const [filter, setFilter] = useState<Filter>("all");
  const [localPosts, setLocalPosts] = useState<FeedPost[]>([]);
  const allPosts = [...localPosts, ...initialFeedPosts];

  const [reactions, setReactions] = useState<Reactions>(() =>
    Object.fromEntries(initialFeedPosts.map((p) => [p.id, { claps: p.claps, fire: p.fire, spark: p.spark }]))
  );
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [feedbackPostId, setFeedbackPostId] = useState<number | null>(null);
  const [isFailModalOpen, setIsFailModalOpen] = useState(false);
  const [royaltyToast, setRoyaltyToast] = useState(false);
  const { getReviewsForPost } = usePeerFeedback();

  const showRoyaltyToast = () => {
    setRoyaltyToast(true);
    setTimeout(() => setRoyaltyToast(false), 3500);
  };

  const react = (postId: number, type: "claps" | "fire" | "spark") => {
    const key = `${postId}-${type}`;
    const isLiked = liked[key];
    setLiked((prev) => ({ ...prev, [key]: !isLiked }));
    setReactions((prev) => ({
      ...prev,
      [postId]: {
        ...(prev[postId] ?? { claps: 0, fire: 0, spark: 0 }),
        [type]: (prev[postId]?.[type] ?? 0) + (isLiked ? -1 : 1),
      },
    }));
  };

  const handleNewFailPost = (post: FeedPost) => {
    setLocalPosts((prev) => [post, ...prev]);
    setReactions((prev) => ({ ...prev, [post.id]: { claps: 0, fire: 0, spark: 0 } }));
    setFilter("fail");
    setIsFailModalOpen(false);
  };

  const filtered = allPosts.filter((p) => {
    if (filter === "success") return p.type !== "fail";
    if (filter === "fail")    return p.type === "fail";
    return true;
  });

  const col1 = filtered.filter((_, i) => i % 2 === 0);
  const col2 = filtered.filter((_, i) => i % 2 === 1);
  const activeFeedbackPost = allPosts.find((p) => p.id === feedbackPostId);

  const failCount = allPosts.filter((p) => p.type === "fail").length;
  const nextId = Math.max(...allPosts.map((p) => p.id), 0) + 1;

  return (
    <div className="min-h-screen bg-[#F2F4F6] font-sans">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-16">

        {/* Page Header */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-black text-[#191F28]">수익·성과 인증 라운지</h1>
            <p className="text-[#191F28]/50 mt-1 text-sm">다른 세오들은 이렇게 돈 벌고 있어요 💸</p>
          </div>
          {/* Write fail post button */}
          <button
            onClick={() => setIsFailModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#191F28] text-white text-xs font-bold rounded-2xl cursor-pointer hover:opacity-90 transition-all whitespace-nowrap hover:-translate-y-0.5"
          >
            <i className="ri-add-line text-sm" />
            💥 실패 공유
          </button>
        </div>

        {/* Story Bar */}
        <div className="mb-6">
          <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
            <div
              onClick={() => setIsFailModalOpen(true)}
              className="flex-shrink-0 flex flex-col items-center gap-2 cursor-pointer"
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white border-2 border-dashed border-[#00D873]">
                <i className="ri-add-line text-[#00D873] text-2xl" />
              </div>
              <span className="text-[10px] text-[#191F28]/60 font-medium whitespace-nowrap">내 기록</span>
            </div>
            {activeUsers.map((u) => (
              <div key={u.id} className="flex-shrink-0 flex flex-col items-center gap-2 cursor-pointer">
                <div className={`p-0.5 rounded-full ${u.isOnline ? "bg-gradient-to-tr from-[#00D873] to-[#00D873]/40" : "bg-[#E0E0E0]"}`}>
                  <div className="bg-white p-0.5 rounded-full">
                    <img src={u.avatar} alt={u.name} className="w-12 h-12 rounded-full object-cover object-top" />
                  </div>
                </div>
                <span className="text-[10px] text-[#191F28]/60 font-medium whitespace-nowrap">{u.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {([
            { key: "all", label: "전체", count: allPosts.length },
            { key: "success", label: "✅ 성공", count: allPosts.length - failCount },
            { key: "fail", label: "💥 실패 공유", count: failCount },
          ] as { key: Filter; label: string; count: number }[]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                filter === tab.key
                  ? tab.key === "fail"
                    ? "bg-red-500 text-white"
                    : "bg-[#191F28] text-white"
                  : "bg-white text-[#8B95A1] hover:bg-[#E8E9EB]"
              }`}
            >
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                filter === tab.key ? "bg-white/20 text-white" : "bg-[#F2F4F6] text-[#8B95A1]"
              }`}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Fail mode banner */}
        {filter === "fail" && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
            <span className="text-xl flex-shrink-0">🧪</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-red-600 mb-0.5">실패에서 가장 많이 배워요</p>
              <p className="text-xs text-[#8B95A1] leading-relaxed">콜브(Kolb)의 경험 학습 이론 — 실패 경험을 공유하고 성찰하면 학습이 2배 빨라져요. 힌트를 주거나 받으며 함께 성장해요.</p>
            </div>
            <button
              onClick={() => setIsFailModalOpen(true)}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white text-xs font-bold rounded-xl cursor-pointer hover:opacity-90 transition whitespace-nowrap"
            >
              <i className="ri-add-line" />
              작성
            </button>
          </div>
        )}

        {/* Normal mode peer review banner */}
        {filter !== "fail" && (
          <div className="bg-gradient-to-r from-[#191F28] to-[#2D3748] rounded-2xl p-5 mb-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#00D873]/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-chat-check-line text-[#00D873] text-xl" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-white">✨ 수익화 프롬프트 쓱- 가져오기</p>
              <p className="text-xs text-white/50 mt-0.5">마음에 드는 포스트에서 프롬프트를 복사해 내 업무에 바로 적용해보세요</p>
            </div>
          </div>
        )}

        {/* Masonry Feed */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-4xl mb-4">💥</p>
            <p className="text-lg font-bold text-[#191F28] mb-2">아직 실패 공유가 없어요</p>
            <p className="text-sm text-[#8B95A1] mb-6">첫 번째로 실패 경험을 공유해보세요. 이게 진짜 용기 있는 학습이에요!</p>
            <button
              onClick={() => setIsFailModalOpen(true)}
              className="px-6 py-3 bg-red-500 text-white font-bold rounded-2xl cursor-pointer hover:opacity-90 transition whitespace-nowrap"
            >
              💥 실패 경험 공유하기
            </button>
          </div>
        ) : (
          <div className="flex gap-4 items-start">
            {[col1, col2].map((col, ci) => (
              <div key={ci} className="flex-1 space-y-4">
                {col.map((post) => {
                  const r = reactions[post.id] ?? { claps: post.claps, fire: post.fire, spark: post.spark };
                  const reviewCount = getReviewsForPost(post.id).length;
                  if (post.type === "fail") {
                    return (
                      <FailCard
                        key={post.id}
                        post={post}
                        onFeedback={() => setFeedbackPostId(post.id)}
                        reactionCounts={r}
                        liked={liked}
                        onReact={(type) => react(post.id, type)}
                      />
                    );
                  }
                  return (
                    <NormalCard
                      key={post.id}
                      post={post}
                      onFeedback={() => setFeedbackPostId(post.id)}
                      reviewCount={reviewCount}
                      reactionCounts={r}
                      liked={liked}
                      onReact={(type) => react(post.id, type)}
                      onCopyPrompt={showRoyaltyToast}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Royalty Toast */}
      <div className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-50 bg-[#191F28] text-white px-5 py-3 rounded-2xl text-sm font-bold flex items-center gap-2 transition-all duration-300 whitespace-nowrap ${royaltyToast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
        <span>💰</span>
        <span>복사 완료! 원작자 세오님께 로열티 10원이 전달돼요</span>
      </div>

      {/* FAB */}
      <button
        onClick={() => setIsFailModalOpen(true)}
        className="fixed bottom-8 right-8 z-40 w-14 h-14 rounded-full bg-[#191F28] text-white flex items-center justify-center cursor-pointer hover:scale-105 transition-all whitespace-nowrap"
        style={{ boxShadow: "0 8px 32px rgba(25,31,40,0.3)" }}
        title="실패 공유 작성"
      >
        <span className="text-xl">💥</span>
      </button>

      {feedbackPostId !== null && activeFeedbackPost && (
        <PeerFeedbackModal
          postId={feedbackPostId}
          postAuthor={activeFeedbackPost.user.name}
          postContent={activeFeedbackPost.content}
          onClose={() => setFeedbackPostId(null)}
        />
      )}

      {isFailModalOpen && (
        <FailPostModal
          onClose={() => setIsFailModalOpen(false)}
          onSubmit={handleNewFailPost}
          nextId={nextId}
        />
      )}
    </div>
  );
}
